import { BRAND } from "@/brand";
import { useState } from "react";
import { Link } from "wouter";
import { useLang } from "@/contexts/LanguageContext";
import { getDict } from "@/lib/i18n";

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const { lang, setLang } = useLang();
  const t = getDict(lang);

  const NAV = [
    { href: "/#metodologia", label: t.nav.metodologia },
    { href: "/#tecnologia", label: t.nav.tecnologia },
    { href: "/equipo", label: t.nav.equipo },
    { href: "/#testimonios", label: t.nav.testimonios },
    { href: "/#colaboradores", label: t.nav.colaboradores },
    { href: "/#faq", label: t.nav.faq },
    { href: "/#contacto", label: t.nav.contacto },
  ];

  const LangToggle = ({ className = "" }: { className?: string }) => (
    <div
      className={`inline-flex items-center rounded-full border border-[var(--cream)]/25 overflow-hidden text-[12px] font-semibold ${className}`}
      role="group"
      aria-label={t.header.langLabel}
    >
      <button
        type="button"
        onClick={() => setLang("es")}
        aria-pressed={lang === "es"}
        className={`px-2.5 py-1 transition-colors ${
          lang === "es"
            ? "bg-[var(--cream)] text-[var(--forest-deep)]"
            : "text-[var(--cream)]/80 hover:text-[var(--cream)]"
        }`}
      >
        ES
      </button>
      <button
        type="button"
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
        className={`px-2.5 py-1 transition-colors ${
          lang === "en"
            ? "bg-[var(--cream)] text-[var(--forest-deep)]"
            : "text-[var(--cream)]/80 hover:text-[var(--cream)]"
        }`}
      >
        EN
      </button>
    </div>
  );

  return (
    <header className="sticky top-0 z-40 bg-[var(--forest)] border-b border-[var(--cream)]/10 text-[var(--cream)]">
      <div className="container flex items-center justify-between h-20 md:h-24 py-3">
        <Link href="/" className="flex items-center shrink-0" aria-label={t.header.home}>
          <img
            src={BRAND.logoStackedCreamTransparent}
            alt="Golf Motion Lab"
            className="h-14 md:h-16 w-auto object-contain"
            draggable={false}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="text-[13px] font-medium tracking-wide text-[var(--cream)]/85 hover:text-[var(--cream)] transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle className="hidden md:inline-flex" />
          <a
            href="#contacto"
            className="hidden md:inline-flex items-center px-5 py-2.5 text-[13px] font-semibold tracking-wide bg-[var(--cream)] text-[var(--forest-deep)] rounded-full hover:bg-white transition-colors"
          >
            {t.header.cta}
          </a>
          <button
            className="lg:hidden h-10 w-10 flex flex-col items-center justify-center gap-1.5"
            onClick={() => setOpen(!open)}
            aria-label={t.header.menu}
          >
            <span className={`h-0.5 w-6 bg-[var(--cream)] transition-transform ${open ? "rotate-45 translate-y-2" : ""}`} />
            <span className={`h-0.5 w-6 bg-[var(--cream)] transition-opacity ${open ? "opacity-0" : ""}`} />
            <span className={`h-0.5 w-6 bg-[var(--cream)] transition-transform ${open ? "-rotate-45 -translate-y-2" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--cream)]/10 bg-[var(--forest-deep)]">
          <nav className="container py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="py-3 text-sm font-medium text-[var(--cream)]/90 hover:text-[var(--cream)]"
              >
                {item.label}
              </a>
            ))}
            <div className="py-3">
              <LangToggle />
            </div>
            <a
              href="#contacto"
              onClick={() => setOpen(false)}
              className="mt-2 py-3 text-sm font-semibold text-[var(--cream)]"
            >
              {t.header.cta} →
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
